import React, { useEffect, useState, useRef, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Switch, useLocation, Route } from 'react-router-dom';
import { throttle } from 'lodash';

import { getAdvisorById, logOut } from '../../data/store/user/userActions';
import {
  getAssetCategories,
  getAssets,
  getDiversification,
} from '../../data/store/instruments/instrumentsActions';

import { PrivateRoute } from '../../PrivateRouter';
import {
  INFORMATION,
  // REPORT,
  SUPPORT,
  DASHBOARD,
} from '../../constants/routes';
import Information from '../Information';
import Profile from '../Profile';
import { permissions } from '../../config/permissions';
import Dashboard from '../Dashboard/Dashboard';
import { Layout } from 'antd';

import ErrorNotification from '../../components/ErrorNotification';
import SuccessNotification from '../../components/SuccessNotification';
import SideBar from '../../components/SideBar/SideBar';

import Routing from './Routing';
import { PrivateRouterWithProps } from '../../PrivateRouterWithProps';
import { getAccountsList } from '../../data/store/account/accountActions';
import QuantRouting from './QuantRouting';
import OrderInstructionsRout from '../SuggestedRoot/OrderInstructionsRout';
import SideBarClient from '../../components/SideBarClient/SideBarClient';
import ProfileClient from '../ProfileClient/ProfileClient';
import SupportClient from '../SupportClient/SupportClient';
import AccountRouting from './AccountRouting';
import { useHistory } from 'react-router-dom';
import Users from '../Users';
import InvateUserForm from '../Users/InvateUserForm/InvateUserForm';
import Support from '../Support/Support';
import ModalTimerLogout from '../../components/ModalTimerLogout/ModalTimerLogout';
import OnboardingRouting from './OnboardingRouting';
import Assistant from '../Assistant/Assistant';
import './style.scss';

const { Content } = Layout;

const IDLE_TIMEOUT = 58 * 60 * 1000; // 58 min
const WARNING_TIMEOUT = 2 * 60 * 1000; // 2 min
const TOTAL_TIMEOUT = IDLE_TIMEOUT + WARNING_TIMEOUT;
const THROTTLE_DELAY = 1000; // 1 sec
const CHECK_INTERVAL = 1000; // 1 sec

const MainRouting = ({ theme }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const history = useHistory();
  // eslint-disable-next-line camelcase
  const { role } = useSelector((state) => state.userReducer.advisor);
  const { list: portfolioList } = useSelector(
    (state) => state.portfolioReducer.portfolioList,
  );
  const accounts = useSelector((state) => state.accountReducer?.accounts?.list);
  const { collapseBar } = useSelector((state) => state.auxiliaryReducer);

  const [userId, setUserId] = useState(null);
  const [needSave, setNeedSave] = useState(false);
  const [saveAlert, setSaveAlert] = useState({ flag: false, path: '' });
  const [optStart, setOptStart] = useState(false);
  const [optAlert, setOptAlert] = useState({ flag: false, path: '' });
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [isOnboarding, setIsOnboarding] = useState(false);

  const [openModalTimerLogout, setOpenModalTimerLogout] = useState(false);
  const [remainingTime, setRemainingTime] = useState(WARNING_TIMEOUT);

  const lastActivityTime = useRef(Date.now());
  const checkInterval = useRef(null);
  const isModalOpenRef = useRef(false);

  const updateActivity = useCallback(() => {
    if (!isModalOpenRef.current) {
      lastActivityTime.current = Date.now();
    }
  }, []);

  const throttledUpdateActivity = useCallback(
    throttle(() => {
      updateActivity();
    }, THROTTLE_DELAY),
    [updateActivity],
  );

  const checkTimeouts = useCallback(() => {
    const elapsed = Date.now() - lastActivityTime.current;

    if (elapsed >= TOTAL_TIMEOUT) {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
      dispatch(logOut());
      return;
    }

    if (elapsed >= IDLE_TIMEOUT && !isModalOpenRef.current) {
      isModalOpenRef.current = true;
      setOpenModalTimerLogout(true);
    }

    if (elapsed >= IDLE_TIMEOUT && isModalOpenRef.current) {
      const remaining = Math.max(0, TOTAL_TIMEOUT - elapsed);
      setRemainingTime(remaining);
    }
  }, [dispatch]);

  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden) {
      const elapsed = Date.now() - lastActivityTime.current;

      if (elapsed >= TOTAL_TIMEOUT) {
        if (checkInterval.current) {
          clearInterval(checkInterval.current);
        }
        dispatch(logOut());
      } else if (elapsed >= IDLE_TIMEOUT && !isModalOpenRef.current) {
        const remaining = Math.max(0, TOTAL_TIMEOUT - elapsed);
        isModalOpenRef.current = true;
        setOpenModalTimerLogout(true);
        setRemainingTime(remaining);
      }
    }
  }, [dispatch]);

  const closeModalTimerLogout = () => {
    isModalOpenRef.current = false;
    setOpenModalTimerLogout(false);
  };

  const resetTimer = () => {
    lastActivityTime.current = Date.now();
    isModalOpenRef.current = false;
    setOpenModalTimerLogout(false);
    setRemainingTime(WARNING_TIMEOUT);
  };

  useEffect(() => {
    setSaveAlert({ flag: false, path: '' });
  }, [pathname]);

  useEffect(() => {
    const preffixOne = pathname.split('/').at(1);
    if (preffixOne === 'onboarding') {
      setIsOnboarding(true);
      return;
    }
    setIsOnboarding(false);
    if (role === 'CLIENT' && accounts?.length && !selectedAccount) {
      setSelectedAccount(accounts[0]?.id);
    }
  }, [role, pathname, accounts]);

  useEffect(() => {
    if (selectedAccount) {
      history.replace(`/account/${selectedAccount}/overview`);
    }
  }, [selectedAccount]);

  useEffect(() => {
    dispatch(getAssets());
    dispatch(getAssetCategories());
    dispatch(getDiversification());
    dispatch(getAdvisorById());
    dispatch(getAccountsList());
    const interval = setInterval(
      () => {
        dispatch(getAdvisorById());
      },
      30 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    lastActivityTime.current = Date.now();

    checkInterval.current = setInterval(checkTimeouts, CHECK_INTERVAL);

    document.addEventListener('mousemove', throttledUpdateActivity);
    document.addEventListener('click', updateActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
      document.removeEventListener('mousemove', throttledUpdateActivity);
      document.removeEventListener('click', updateActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      throttledUpdateActivity.cancel?.();
    };
  }, [
    checkTimeouts,
    throttledUpdateActivity,
    updateActivity,
    handleVisibilityChange,
  ]);

  const {
    SUPER_ADMIN,
    ADVISER,
    CLIENT,
    ADVISER_NOT_SECURE,
    CLIENT_NOT_SECURE,
  } = permissions.roles;

  return (
    <>
      {role ? (
        <Layout className="layout-main">
          {!isOnboarding && (
            <>
              {role === 'CLIENT_NOT_SECURE' || role === 'CLIENT' ? (
                <SideBarClient
                  theme={theme}
                  selectedAccount={selectedAccount}
                  setSelectedAccount={setSelectedAccount}
                />
              ) : (
                <SideBar
                  userId={userId}
                  setUserId={setUserId}
                  needSave={needSave}
                  saveAlert={saveAlert}
                  optAlert={optAlert}
                  setSaveAlert={setSaveAlert}
                  setOptAlert={setOptAlert}
                  optStart={optStart}
                  theme={theme}
                />
              )}
            </>
          )}
          <Layout>
            {role === 'CLIENT_NOT_SECURE' || role === 'CLIENT' ? (
              <Content className="client content-row">
                <Switch>
                  <PrivateRoute
                    exact
                    path={SUPPORT}
                    roles={[CLIENT_NOT_SECURE, CLIENT]}
                  >
                    <SupportClient />
                  </PrivateRoute>
                  <PrivateRoute
                    exact
                    path={'/settings'}
                    roles={[CLIENT, CLIENT_NOT_SECURE]}
                  >
                    <ProfileClient />
                  </PrivateRoute>
                  <PrivateRoute
                    path={'/account/:accountId'}
                    roles={[CLIENT, CLIENT_NOT_SECURE]}
                  >
                    <AccountRouting />
                  </PrivateRoute>
                  <Route exact path="/">
                    <Redirect to="/account" />
                  </Route>
                </Switch>
              </Content>
            ) : (
              <Content
                className={`client content-row ${collapseBar ? 'collapse' : ''}`}
              >
                <Switch>
                  <PrivateRouterWithProps
                    path="/assistant"
                    roles={[ADVISER, SUPER_ADMIN]}
                    customProps={{
                      theme, pathname, portfolioList
                    }}
                    component={Assistant}
                  />
                  <PrivateRouterWithProps
                    path="/accounts"
                    roles={[ADVISER, SUPER_ADMIN, CLIENT]}
                    customProps={{
                      theme,
                      setUserId,
                      needSave,
                      setNeedSave,
                      saveAlert,
                      setSaveAlert,
                      optAlert,
                      setOptAlert,
                      setOptStart,
                    }}
                    component={Routing}
                  />
                  <PrivateRouterWithProps
                    path="/quant"
                    roles={[SUPER_ADMIN]}
                    customProps={{
                      needSave,
                      setNeedSave,
                      saveAlert,
                      setSaveAlert,
                    }}
                    component={QuantRouting}
                  />
                  <PrivateRouterWithProps
                    path="/onboarding"
                    roles={[SUPER_ADMIN, ADVISER]}
                    customProps={{
                      theme,
                      needSave,
                      setNeedSave,
                      saveAlert,
                      setSaveAlert,
                    }}
                    component={OnboardingRouting}
                  />
                  <PrivateRouterWithProps
                    path={'/users/edit-user/:userId'}
                    roles={[SUPER_ADMIN, ADVISER]}
                    component={InvateUserForm}
                    customProps={{
                      needSave,
                      setNeedSave,
                      saveAlert,
                      setSaveAlert,
                    }}
                  />
                  <PrivateRouterWithProps
                    path={'/users/invite-advisor'}
                    roles={[SUPER_ADMIN, ADVISER]}
                    component={InvateUserForm}
                    customProps={{
                      needSave,
                      setNeedSave,
                      saveAlert,
                      setSaveAlert,
                    }}
                  />
                  <PrivateRoute
                    path={'/users/users-list'}
                    roles={[SUPER_ADMIN, ADVISER]}
                    component={Users}
                  />
                  <PrivateRoute
                    exact
                    path={SUPPORT}
                    roles={[SUPER_ADMIN, ADVISER, CLIENT]}
                    component={Support}
                  />
                  <Route exact path={'/admin-order-instructions'}>
                    <Redirect to={'/admin-order-instructions/approved'} />
                  </Route>
                  <PrivateRouterWithProps
                    path={'/admin-order-instructions/:typeTab'}
                    roles={[SUPER_ADMIN]}
                    component={OrderInstructionsRout}
                  />
                  <PrivateRoute
                    exact
                    path={'/settings'}
                    roles={[ADVISER, SUPER_ADMIN, ADVISER_NOT_SECURE]}
                    component={Profile}
                  />
                  <PrivateRoute
                    exact
                    path={INFORMATION}
                    roles={[SUPER_ADMIN, ADVISER]}
                    component={Information}
                  />
                  <PrivateRoute
                    exact
                    path={DASHBOARD}
                    roles={[SUPER_ADMIN, ADVISER, CLIENT]}
                    component={Dashboard}
                  />
                  {/* <PrivateRoute
                    exact
                    path={MY_DETAILS}
                    roles={[SUPER_ADMIN, ADVISER, CLIENT]}
                    component={MyDetails}
                  /> */}

                  {role && role === 'CLIENTT' ? (
                    // eslint-disable-next-line camelcase
                    <Redirect to={'/acount'} />
                  ) : (
                    <PrivateRoute
                      exact
                      path="*"
                      roles={[ADVISER, SUPER_ADMIN, CLIENT]}
                      component={Routing}
                    >
                      <Redirect to={'/accounts'} />
                    </PrivateRoute>
                  )}
                </Switch>
              </Content>
            )}
            <ErrorNotification />
            <SuccessNotification />
          </Layout>
        </Layout>
      ) : isOnboarding ? (
        <OnboardingRouting />
      ) : (
        <div />
      )}
      <ModalTimerLogout
        open={openModalTimerLogout}
        closeModal={closeModalTimerLogout}
        resetTimer={resetTimer}
        remainingTime={remainingTime}
      />
    </>
  );
};

export default MainRouting;

MainRouting.propTypes = {
  theme: PropTypes.string,
};
